import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Tooltip as LTooltip, CircleMarker } from "react-leaflet";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Path, ArrowsClockwise, Funnel, MapPinLine, IdentificationBadge,
} from "@phosphor-icons/react";

// Depot → hex color (visually distinguishable on the map)
const DEPOT_COLORS = {
  "Panaji":   "#1E3A8A",
  "Mapusa":   "#DC2626",
  "Margao":   "#F59E0B",
  "Vasco":    "#10B981",
  "Ponda":    "#8B5CF6",
  "Canacona": "#EC4899",
};
const CAT_COLORS = {
  "Express":     "#1E3A8A",
  "Mofussil":    "#10B981",
  "Shuttle":     "#F59E0B",
  "Airport":     "#DC2626",
  "Inter-State": "#8B5CF6",
};

export default function NetworkMap() {
  const depotMap = useDepots();
  const [polylines, setPolylines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(80);
  const [depot, setDepot] = useState("");
  const [category, setCategory] = useState("");
  const [colorBy, setColorBy] = useState("depot"); // "depot" or "category"
  const [hovered, setHovered] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("limit", limit);
      if (depot) qs.set("depot", depot);
      if (category) qs.set("category", category);
      const { data } = await api.get(`/network/polylines?${qs.toString()}`);
      setPolylines(data);
      toast.success(`Loaded ${data.length} route polylines`);
    } catch (e) {
      toast.error("Failed to load polylines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Compute legend chips based on color scheme
  const legendEntries = useMemo(() => {
    const counts = {};
    for (const p of polylines) {
      const key = colorBy === "depot" ? p.depot : p.category;
      counts[key] = (counts[key] || 0) + 1;
    }
    const palette = colorBy === "depot" ? DEPOT_COLORS : CAT_COLORS;
    return Object.entries(counts)
      .filter(([k]) => k)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ label: k, count: v, color: palette[k] || "#64748B" }));
  }, [polylines, colorBy]);

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="network-map-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Path size={24} className="text-violet-700"/> Network Map
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          All {polylines.length} KTCL routes plotted from the official GTFS feed — each polyline is the actual physical path the bus follows, colored by {colorBy}.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center bg-card border rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Funnel size={14} className="text-muted-foreground"/>
          <select value={depot} onChange={(e) => setDepot(e.target.value)} data-testid="network-depot-filter"
            className="px-2 py-1.5 border border-slate-200 rounded text-sm">
            <option value="">All Depots</option>
            {Object.keys(DEPOT_COLORS).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="network-cat-filter"
            className="px-2 py-1.5 border border-slate-200 rounded text-sm">
            <option value="">All Categories</option>
            {Object.keys(CAT_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input type="number" value={limit} onChange={(e) => setLimit(+e.target.value || 80)}
            data-testid="network-limit" min={5} max={500}
            className="w-20 h-8 text-sm" placeholder="80"/>
          <Button onClick={load} disabled={loading} data-testid="network-reload" size="sm">
            <ArrowsClockwise size={14} className="mr-1"/> {loading ? "Loading…" : "Apply"}
          </Button>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs font-semibold text-slate-500 mr-1">Color by:</span>
          {[
            { k: "depot", l: "Depot" },
            { k: "category", l: "Category" },
          ].map((o) => (
            <button key={o.k} onClick={() => setColorBy(o.k)} data-testid={`network-color-${o.k}`}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition-colors
                ${colorBy === o.k ? "bg-[#1E3A8A] text-white" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      {legendEntries.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center bg-card border rounded-lg p-3" data-testid="network-legend">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Legend:</span>
          {legendEntries.map((e) => (
            <span key={e.label} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 bg-slate-50 border border-slate-200 rounded">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: e.color }}/>
              {e.label} <span className="text-slate-500 font-normal">({e.count})</span>
            </span>
          ))}
        </div>
      )}

      {/* Map */}
      <div className="bg-card border rounded-lg overflow-hidden" style={{ height: "70vh", minHeight: 480 }}>
        <MapContainer center={[15.39, 73.94]} zoom={10} style={{ height: "100%", width: "100%" }}
          data-testid="network-map-container">
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
          {polylines.map((p) => {
            const palette = colorBy === "depot" ? DEPOT_COLORS : CAT_COLORS;
            const colorKey = colorBy === "depot" ? p.depot : p.category;
            const color = palette[colorKey] || "#64748B";
            const isHover = hovered === p.route_id;
            return (
              <Polyline
                key={p.route_id}
                positions={p.polyline}
                pathOptions={{
                  color,
                  weight: isHover ? 6 : 3,
                  opacity: isHover ? 1 : 0.55,
                  lineCap: "round",
                  lineJoin: "round",
                }}
                eventHandlers={{
                  mouseover: () => setHovered(p.route_id),
                  mouseout: () => setHovered(null),
                }}>
                <LTooltip sticky direction="top" offset={[0, -2]}>
                  <div className="text-xs">
                    <div className="font-extrabold text-violet-700">{p.route_id}</div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-slate-500 mt-0.5">
                      {fmtDepot(p.depot, depotMap)} · {p.category} · {p.stops} stops
                    </div>
                  </div>
                </LTooltip>
              </Polyline>
            );
          })}
        </MapContainer>
      </div>

      {/* Footer hint */}
      <div className="text-xs text-muted-foreground">
        Tip: hover any line to highlight a single route. Source — official KTCL GTFS feed ({polylines.length} of 476 routes shown).
      </div>
    </div>
  );
}
